import classNames from 'classnames'

import {
  BriefingArrowIcon,
  BriefingCarIcon,
  BriefingInfoIcon,
  BriefingTransportIcon,
  CommonWrite3Icon,
} from '@local/assets/icons'
import { CLICK_CODE } from '@local/utils'

import { ClickEvent } from '@web/types'
import Clickable from '@web/ui/components/Clickable'

import styles from './DailyLocation.module.scss'

const getLocationIcon = (cardType: CardTypes, isDirectionProvided: boolean) => {
  if (cardType === CardTypes.USED) return CommonWrite3Icon

  if (cardType === CardTypes.NAVIGATION && isDirectionProvided) return BriefingCarIcon

  if (cardType === CardTypes.TRANSPORT && isDirectionProvided) return BriefingTransportIcon

  return BriefingInfoIcon
}

const getLocationLabel = (cardType: CardTypes, isDirectionProvided: boolean) => {
  if (cardType === CardTypes.USED) return '리뷰 쓰기'

  if (cardType === CardTypes.NAVIGATION && isDirectionProvided) return '네비게이션'

  if (cardType === CardTypes.TRANSPORT && isDirectionProvided) return '대중교통'

  return '안내'
}

const getWhichTransporationToUseLabel = (publicType: string) => {
  if (publicType === 'BUS') return '버스 이용'

  if (publicType === 'SUBWAY') return '지하철 이용'

  if (publicType === 'BUS_AND_SUBWAY') return '버스 + 지하철 이용'

  return '길찾기를 확인해주세요'
}

export interface PrimitiveCarDirection {
  duration: number | null
  fare: number | null
  roadName: string | null
}

export interface PrimitivePublicTransitDirection {
  duration: number | null
  fare: number | null
  type: string | null
}

enum CardTypes {
  NAVIGATION = 'NAVIGATION',
  TRANSPORT = 'TRANSPORT',
  USED = 'USED',
}

type DailyLocationProps =
  | {
      cardType: CardTypes.NAVIGATION
      direction?: PrimitiveCarDirection
      onClickCard: () => void
      isLoading: boolean
      clickCode: string
    }
  | {
      cardType: CardTypes.TRANSPORT
      direction?: PrimitivePublicTransitDirection
      onClickCard: () => void
      isLoading: boolean
      clickCode: string
    }
  | {
      cardType: CardTypes.USED
      onClickCard: (e: ClickEvent) => void
      isLoading: boolean
      clickCode: string
    }

function DailyLocationCard(props: DailyLocationProps) {
  const { cardType, onClickCard, isLoading, clickCode } = props
  const direction = 'direction' in props ? props.direction : undefined

  const locationLabel = getLocationLabel(cardType, isLoading || !!direction)
  const LocationIcon = getLocationIcon(cardType, isLoading || !!direction)

  const isUsed = cardType === CardTypes.USED
  const isTransport = cardType === CardTypes.TRANSPORT && !!direction
  const isNavigation = cardType === CardTypes.NAVIGATION && !!direction
  const isNoTransport = cardType === CardTypes.TRANSPORT && !direction
  const isNoNavigation = cardType === CardTypes.NAVIGATION && !direction

  const durationMin = direction?.duration ? direction.duration % 60 : null
  const durationHour = direction?.duration ? Math.floor(direction.duration / 60) : null
  const durationString = `${durationHour ? `${durationHour}시간 ` : ''}${durationMin ? `${durationMin}분` : ''}`

  return (
    <div className={styles.direction_card}>
      <Clickable className={styles.link} nClickCode={clickCode} onClick={onClickCard}>
        <div className={styles.tit_area}>
          <span
            className={classNames(styles.icon_area, {
              [styles.type_info]: !isUsed && !direction,
              [styles.type_loading]: isLoading,
            })}
          >
            <LocationIcon className={styles.icon} aria-hidden="true" />
            <span className="place_blind">{locationLabel}</span>
          </span>
          {(isUsed || durationString) && (
            <>
              <strong className={styles.title}>{isUsed ? '방문 경험은 어떠셨나요?' : <>{durationString}</>}</strong>
              <BriefingArrowIcon className={styles.icon_arrow} aria-hidden="true" />
            </>
          )}
        </div>
        <div className={styles.info_area}>
          {
            <div
              className={classNames(styles.info, {
                [styles.type_multi]: isNoTransport || isLoading,
                [styles.type_loading]: isLoading,
              })}
            >
              {!isUsed && isLoading && (
                <>
                  길찾기 정보를
                  <br />
                  찾고 있어요.
                </>
              )}
              {!isLoading && isNoTransport && (
                <>
                  지금 이용할 수 있는
                  <br />
                  대중교통이 없습니다.
                </>
              )}
              {!isLoading && isNoNavigation && (
                <>
                  내비게이션 경로
                  <br />
                  탐색 정보가 없습니다.
                </>
              )}
              {!isLoading && isNavigation && <>{(direction as PrimitiveCarDirection).roadName}</>}
              {!isLoading && isTransport && (
                <>{getWhichTransporationToUseLabel((direction as PrimitivePublicTransitDirection).type || '')}</>
              )}
            </div>
          }
          {(isUsed || (!isLoading && (isTransport || isNavigation))) && (
            <div className={classNames(styles.sub_info, { [styles.type_multi]: isUsed })}>
              {isUsed && (
                <>
                  오늘의 경험을 기록으로 남겨보세요.
                  <br />
                  남겨주신 리뷰는 다음 방문자들에게 큰 도움이 됩니다.
                </>
              )}
              {(isNavigation || isTransport) && direction.fare != null && direction.fare > 0 && (
                <>
                  <span className={classNames({ place_blind: isLoading })}>
                    {isNavigation && '통행료' + ' ' + (direction as PrimitiveCarDirection).fare + '원'}
                    {isTransport && '요금' + ' ' + (direction as PrimitivePublicTransitDirection).fare + '원'}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </Clickable>
    </div>
  )
}

interface UsedProps {
  isLoading: boolean
  onClickUsedCard: (e: ClickEvent) => void
}

export const UsedDailyLocation = ({ isLoading, onClickUsedCard }: UsedProps) => (
  <div className={styles.direction_area}>
    <DailyLocationCard
      isLoading={isLoading}
      cardType={CardTypes.USED}
      onClickCard={onClickUsedCard}
      clickCode={CLICK_CODE['mydaily.reviewrite']}
    />
  </div>
)

interface DirectionsProps {
  onClickFindFastestCarPath: () => void
  onClickFindFastestPublicTransitPath: () => void
  fastestCarPathUrl?: string
  fastestPublicTransitPathUrl?: string
  directions: {
    car: PrimitiveCarDirection
    publicTransit: PrimitivePublicTransitDirection
  } | null
  isLoading: boolean
}

export const DirectionsDailyLocation = ({
  onClickFindFastestCarPath,
  onClickFindFastestPublicTransitPath,
  directions,
  isLoading,
}: DirectionsProps) => {
  return (
    <div className={styles.direction_area}>
      <DailyLocationCard
        isLoading={isLoading}
        onClickCard={() => onClickFindFastestCarPath()}
        cardType={CardTypes.NAVIGATION}
        direction={directions?.car}
        clickCode={CLICK_CODE['mydaily.navigation']}
      />
      <DailyLocationCard
        isLoading={isLoading}
        onClickCard={() => onClickFindFastestPublicTransitPath()}
        cardType={CardTypes.TRANSPORT}
        direction={directions?.publicTransit}
        clickCode={CLICK_CODE['mydaily.pubtrans']}
      />
    </div>
  )
}
